import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import '../styles/home.css';

const fieldClass =
  'w-full bg-white border border-[#EAF1EC] rounded-xl px-6 py-4 focus:outline-none focus:ring-2 focus:ring-[#174C3C]/20 text-lg font-medium';
const labelClass = 'text-sm font-black text-[#17211E] uppercase tracking-widest';

const START_OPTIONS = [
  {
    id: 'care',
    label: 'Monthly website care',
    text: 'Updates, backups, monitoring, and someone watching the site.',
    helpType: 'Get Started',
    detailLabel: 'Preferred plan',
    details: [
      'Essential Care',
      'Managed Care',
      'Advanced Care',
      'Not sure — quote after you look'
    ]
  },
  {
    id: 'support',
    label: 'Technical support',
    text: 'A broken site, DNS, email, or a one-off job. Hourly, prepaid, or a retainer.',
    helpType: 'Support',
    detailLabel: 'How do you want to buy?',
    details: [
      'Hourly',
      'Prepaid block',
      'Monthly retainer',
      'Not sure — quote after you look'
    ]
  },
  {
    id: 'server',
    label: 'Server management',
    text: 'cPanel/WHM, LiteSpeed, CloudLinux, Imunify360. The server, not WordPress care.',
    helpType: 'Server management',
    detailLabel: 'What do you need?',
    details: [
      'Ongoing server management',
      'One-time troubleshooting',
      'Not sure — quote after you look'
    ]
  }
];

const CARE_CARDS = [
  { name: 'Essential Care', text: 'Updates, backups, monitoring, and a monthly care note.' },
  { name: 'Managed Care', text: 'The same foundation, plus included support time.' },
  { name: 'Advanced Care', text: 'More hands-on time for WooCommerce or a closer watch.' }
];

function startFromQuery(params) {
  const start = String(params.get('start') || '').toLowerCase();
  if (start === 'support') return 'support';
  if (start === 'server') return 'server';
  if (start === 'care') return 'care';

  const plan = String(params.get('plan') || '').toLowerCase();
  if (plan.includes('essential') || plan.includes('managed') || plan.includes('advanced')) {
    return 'care';
  }
  return 'care';
}

function detailFromQuery(params, startId) {
  const plan = String(params.get('plan') || '');
  const option = START_OPTIONS.find((item) => item.id === startId);
  if (!option) return '';
  const match = option.details.find((detail) => detail.toLowerCase() === plan.toLowerCase());
  if (match) return match;
  if (startId === 'care') {
    const v = plan.toLowerCase();
    if (v.includes('essential')) return 'Essential Care';
    if (v.includes('managed')) return 'Managed Care';
    if (v.includes('advanced')) return 'Advanced Care';
  }
  return '';
}

const GetStarted = () => {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [startId, setStartId] = useState('care');
  const [preferredDetail, setPreferredDetail] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    const nextStart = startFromQuery(searchParams);
    setStartId(nextStart);
    setPreferredDetail(detailFromQuery(searchParams, nextStart));
  }, [searchParams]);

  const startOption = START_OPTIONS.find((item) => item.id === startId) || START_OPTIONS[0];

  const chooseStart = (id) => {
    setStartId(id);
    setPreferredDetail('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());
    payload.audience = 'business';
    payload.help_type = startOption.helpType;
    payload.message = `Start with: ${startOption.label}\nPreference: ${payload.preferred_detail}\n\n${payload.message}`;
    delete payload.preferred_detail;
    delete payload.start_with;

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        throw new Error(result.error || 'The message could not be sent.');
      }

      setSubmitted(true);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'contact',
          event_label: `get-started-${startId}`,
        });
      }
    } catch (error) {
      setSubmitError(error.message || 'The message could not be sent.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full min-h-[70vh] flex items-center justify-center bg-[#FAF8F3] px-4">
        <div className="max-w-2xl w-full bg-white p-12 md:p-20 rounded-[3rem] border border-[#EAF1EC] shadow-xl text-center">
          <div className="w-20 h-20 bg-[#EAF1EC] text-[#174C3C] rounded-full flex items-center justify-center mx-auto mb-10">
            <SafeIcon icon={FiCheck} className="w-10 h-10" />
          </div>
          <h2 className="text-4xl font-extrabold text-[#17211E] mb-6">You will hear from Dave</h2>
          <p className="text-xl text-[#17211E]/75 font-medium leading-relaxed mb-12">
            He will look at the setup, confirm the fit, and send the quote. Usually within one business day.
          </p>
          <Link to="/" className="home-button">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="bg-[#FAF8F3] pt-24 pb-16 lg:pt-36 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Get started</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10 max-w-4xl">
            Start with the work you actually need.
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed max-w-3xl font-medium">
            Monthly website care, technical support, or the server. Pick one, send the URL, and Dave confirms the fit and the price.
          </p>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-16 lg:grid-cols-[1.1fr_.9fr]">
          <div>
            <h2 className="text-4xl font-bold text-[#17211E] mb-10">What do you want to start?</h2>
            <div className="space-y-4">
              {START_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => chooseStart(option.id)}
                  className={`w-full text-left border p-6 transition-colors ${
                    startId === option.id
                      ? 'border-[#174C3C] bg-[#EAF1EC]'
                      : 'border-[#17211E]/15 bg-[#FAF8F3] hover:border-[#174C3C]'
                  }`}
                >
                  <span className="block font-bold text-[#17211E] mb-1">{option.label}</span>
                  <span className="text-[#17211E]/70">{option.text}</span>
                </button>
              ))}
            </div>

            {startId === 'care' && (
              <div className="mt-10 space-y-4">
                {CARE_CARDS.map((plan) => (
                  <button
                    key={plan.name}
                    type="button"
                    onClick={() => setPreferredDetail(plan.name)}
                    className={`w-full text-left border p-6 transition-colors ${
                      preferredDetail === plan.name
                        ? 'border-[#174C3C] bg-[#EAF1EC]'
                        : 'border-[#17211E]/15 bg-[#FAF8F3] hover:border-[#174C3C]'
                    }`}
                  >
                    <span className="block font-bold text-[#17211E] mb-1">{plan.name}</span>
                    <span className="text-[#17211E]/70">{plan.text}</span>
                  </button>
                ))}
              </div>
            )}

            <ol className="mt-12 space-y-8">
              <li>
                <span className="block font-bold text-[#17211E] mb-2">1. You say what you want</span>
                <p className="text-lg leading-relaxed text-[#17211E]/75">
                  Care, support, or the server. No logins needed to start.
                </p>
              </li>
              <li>
                <span className="block font-bold text-[#17211E] mb-2">2. Dave confirms</span>
                <p className="text-lg leading-relaxed text-[#17211E]/75">
                  He looks at the setup and sends the quote.
                </p>
              </li>
              <li>
                <span className="block font-bold text-[#17211E] mb-2">3. We take it on</span>
                <p className="text-lg leading-relaxed text-[#17211E]/75">
                  After you say yes, we onboard and you get a phone number and support desk.
                </p>
              </li>
            </ol>
            <p className="mt-10 text-lg text-[#17211E]/60 font-medium">
              Not sure which door?{' '}
              <Link
                to="/digital-review"
                className="font-bold text-[#174C3C] underline decoration-2 underline-offset-4"
              >
                Request a Digital Review
              </Link>
              .
            </p>
            <p className="mt-6 text-lg text-[#17211E]/60 font-medium">
              Agencies:{' '}
              <Link
                to="/contact?audience=agency"
                className="font-bold text-[#174C3C] underline decoration-2 underline-offset-4"
              >
                use the agency form
              </Link>
              {' '}for white-label pricing.
            </p>
          </div>

          <div className="bg-[#FAF8F3] rounded-[2.5rem] border border-[#EAF1EC] p-8 sm:p-12">
            <h2 className="text-3xl font-extrabold text-[#17211E] mb-8">Get started</h2>
            <form method="POST" onSubmit={handleSubmit} className="space-y-8">
              <input type="hidden" name="audience" value="business" />
              <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label htmlFor="start-company-fax">Company fax</label>
                <input id="start-company-fax" type="text" name="company_fax" tabIndex="-1" autoComplete="off" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>Full Name *</label>
                <input type="text" name="name" required className={fieldClass} placeholder="Your name" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>Email Address *</label>
                <input type="email" name="email" required className={fieldClass} placeholder="you@company.com" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>Company name *</label>
                <input type="text" name="company" required className={fieldClass} placeholder="Your company" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>
                  {startId === 'server' ? 'Server or website URL *' : 'Website URL *'}
                </label>
                <input type="url" name="url" required className={fieldClass} placeholder="https://" />
              </div>

              <div className="space-y-3">
                <label className={labelClass}>What do you want to start? *</label>
                <select
                  name="start_with_select"
                  required
                  className={`${fieldClass} appearance-none`}
                  value={startId}
                  onChange={(e) => chooseStart(e.target.value)}
                >
                  {START_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <label className={labelClass}>{startOption.detailLabel} *</label>
                <select
                  name="preferred_detail"
                  required
                  className={`${fieldClass} appearance-none`}
                  value={preferredDetail}
                  onChange={(e) => setPreferredDetail(e.target.value)}
                >
                  <option value="">Select an option</option>
                  {startOption.details.map((detail) => (
                    <option key={detail} value={detail}>
                      {detail}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <label className={labelClass}>What is going on? *</label>
                <textarea
                  name="message"
                  required
                  rows="4"
                  className={fieldClass}
                  placeholder="A short note is enough. What should we take over?"
                />
              </div>

              <div>
                {submitError && (
                  <p className="mb-6 text-center text-lg font-bold text-[#8A2B2B]">{submitError}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="home-button w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : 'Get started'}
                </button>
                <p className="text-center mt-6 text-[#17211E]/50 font-bold italic">
                  Dave usually replies within one business day.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetStarted;
